// @file: client/src/components/PromptLibraryModal.jsx
import { useState, useEffect } from "react";
const API_BASE = import.meta.env.VITE_API_URL || "";
const PROMPTS_CACHE_KEY = "okeybot_custom_prompts";

const DEFAULT_PROMPTS = [
  { id: "def_1", title: "General Readability", content: "Format all output for a narrow chat interface: Do not use tables or markdown headers (#). Use **Bold Text** for headings and bulleted lists for structured data. If presenting comparison data, use a code block or a list. Ensure all content is legible without horizontal scrolling." },
  { id: "def_2", title: "Coding Readability", content: "Always wrap code in language-specific blocks (e.g., ` ```js ` or ` ```py `). When providing code diffs, use ` ```diff ` blocks marking only modified lines with +/-. Include enough surrounding context lines to make the change easy to locate. Never use placeholders; ensure all code is copy-paste ready." },
  { id: "def_3", title: "Concise & On-Point", content: "Give short, concise, on-point answers. Only answer what the user asked." },
];

export default function PromptLibraryModal({ onClose, onInsert }) {
  const [customPrompts, setCustomPrompts] = useState(() => {
    try {
      const cached = localStorage.getItem(PROMPTS_CACHE_KEY);
      return cached ? JSON.parse(cached) : [];
    } catch {
      return [];
    }
  });
  const [isCreating, setIsCreating] = useState(false);
  const [isLoading, setIsLoading] = useState(false); // For saving actions
  const [isFetching, setIsFetching] = useState(true); // For initial load
  const [editingId, setEditingId] = useState(null);
  const [newTitle, setNewTitle] = useState("");
  const [newContent, setNewContent] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    fetchPrompts();
  }, []);

  const fetchPrompts = () => {
    return fetch(`${API_BASE}/api/prompts`, { credentials: "include" })
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        if (Array.isArray(data)) {
          setCustomPrompts(data);
          localStorage.setItem(PROMPTS_CACHE_KEY, JSON.stringify(data));
        }
      })
      .catch(console.error)
      .finally(() => setIsFetching(false));
  };

  const saveCustomPrompt = (prompt) => {
    setIsLoading(true);
    const isUpdate = !!prompt.id;


    // Optimistic Update: Update UI & Cache immediately
    setCustomPrompts(prev => {
      const tempId = prompt.id || `temp_${Date.now()}`;
      const optimisticItem = { ...prompt, id: tempId };
      const next = isUpdate
        ? prev.map(p => p.id === prompt.id ? optimisticItem : p)
        : [optimisticItem, ...prev];

      localStorage.setItem(PROMPTS_CACHE_KEY, JSON.stringify(next));
      return next;
    });

    const url = isUpdate
      ? `${API_BASE}/api/prompts/${prompt.id}`
      : `${API_BASE}/api/prompts`;

    fetch(url, {
      method: isUpdate ? "PUT" : "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(prompt),
      credentials: "include"
    })
      .then((res) => {
        // Only refetch on create to get the real DB ID
        if (!isUpdate && res.ok) fetchPrompts();
      })
      .finally(() => setIsLoading(false));
  };

  const deleteCustomPrompt = (id) => {
    // Optimistic update: remove instantly from UI
    setCustomPrompts(prev => {
      const next = prev.filter(p => p.id !== id);
      localStorage.setItem(PROMPTS_CACHE_KEY, JSON.stringify(next));
      return next;
    });
    setDeletingId(null);

    fetch(`${API_BASE}/api/prompts/${id}`, {
      method: "DELETE",
      credentials: "include"
    }).catch(console.error);
  };

  const handleSave = () => {
    if (!newTitle.trim() || !newContent.trim()) return;

    if (editingId) {
      saveCustomPrompt({ id: editingId, title: newTitle, content: newContent });
    } else {
      saveCustomPrompt({ title: newTitle, content: newContent });
    }

    setNewTitle("");
    setNewContent("");
    setEditingId(null);
    setIsCreating(false);
  };

  const handleEdit = (p, e) => {
    e.stopPropagation();
    setNewTitle(p.title);
    setNewContent(p.content);
    setEditingId(p.id);
    setIsCreating(true);
  };

  const initiateDelete = (id, e) => {
    e.stopPropagation();
    setDeletingId(id);
  };

  return (
    <>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content prompt-library-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: "700px" }}>
          <div className="modal-header">
            <h2>Prompt Library</h2>
            <button className="modal-close" onClick={onClose}>✕</button>
          </div>
          <div className="modal-body">

            {!isCreating ? (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                    Select a prompt to append to your settings.
                  </span>
                  <button
                    className="btn btn-secondary"
                    style={{ padding: '0.4rem 0.75rem', fontSize: '0.75rem', display: 'flex', gap: '0.4rem' }}
                    onClick={() => {
                      setEditingId(null);
                      setNewTitle("");
                      setNewContent("");
                      setIsCreating(true);
                    }}
                  >
                    <span>+</span> New Custom Prompt
                  </button>
                </div>

                <div className="prompt-list">
                  {/* Custom Prompts Section */}

                  {customPrompts.length > 0 && (
                    <>
                      <div style={{ gridColumn: "1/-1", fontSize: "0.75rem", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--accent)", marginTop: "0.5rem" }}>
                        My Prompts
                      </div>
                      {customPrompts.map(p => (
                        <div key={p.id} className="prompt-card" onClick={() => onInsert(p.content)}>
                          <div className="prompt-card-header">
                            <strong>{p.title}</strong>
                            <div style={{ display: 'flex', gap: '0.25rem' }}>
                              <button className="btn-icon-secondary" onClick={(e) => handleEdit(p, e)} title="Edit">✎</button>
                              <button className="btn-icon-danger" onClick={(e) => initiateDelete(p.id, e)} title="Delete">✕</button>
                            </div>
                          </div>
                          <div className="prompt-preview">{p.content}</div>
                          <div className="prompt-card-footer">Click to Append</div>
                        </div>
                      ))}
                      <div className="divider" style={{ gridColumn: "1/-1", margin: "1rem 0 0.5rem 0" }}></div>
                    </>
                  )}

                  {/* Ready-Made Section */}
                  <div style={{ gridColumn: "1/-1", fontSize: "0.75rem", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--text-muted)", marginTop: "0.5rem" }}>
                    Ready-Made
                  </div>
                  {DEFAULT_PROMPTS.map(p => (
                    <div key={p.id} className="prompt-card" onClick={() => onInsert(p.content)}>
                      <div className="prompt-card-header">
                        <strong>{p.title}</strong>
                      </div>
                      <div className="prompt-preview">{p.content}</div>
                      <div className="prompt-card-footer">Click to Append</div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="card" style={{ padding: "1.5rem", border: "1px solid var(--accent)" }}>
                <h3 style={{ marginBottom: "1rem", fontSize: "1.1rem" }}>
                  {editingId ? "Edit Prompt" : "Create Custom Prompt"}
                </h3>
                <div className="form-group">
                  <label className="form-label">Title</label>
                  <input
                    className="input"
                    value={newTitle}
                    onChange={e => setNewTitle(e.target.value)}
                    placeholder="e.g. Custom Prompt Name"
                    autoFocus
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Prompt Content</label>
                  <textarea
                    className="input"
                    rows={4}
                    value={newContent}
                    onChange={e => setNewContent(e.target.value)}
                    placeholder="Enter the system prompt text..."
                  />
                </div>
                <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
                  <button className="btn btn-secondary" onClick={() => setIsCreating(false)}>Cancel</button>
                  <button
                    className="btn btn-primary"
                    onClick={handleSave}
                    disabled={!newTitle.trim() || !newContent.trim() || isLoading}
                  >
                    {isLoading ? 'Saving...' : 'Save Prompt'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {deletingId && (
        <div className="modal-overlay" style={{ zIndex: 3200 }} onClick={() => setDeletingId(null)}>
          <div className="modal-content" style={{ maxWidth: "400px" }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Delete Prompt?</h2>
            </div>
            <div className="modal-body">
              <p style={{ color: "var(--text-secondary)" }}>
                Are you sure you want to delete this prompt? This action cannot be undone.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setDeletingId(null)} disabled={isDeleting}>Cancel</button>
              <button className="btn btn-danger" onClick={() => deleteCustomPrompt(deletingId)} disabled={isDeleting}>
                {isDeleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
